import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/buttons/button";
import { TextLink } from "../../components/buttons/textLink";
import { TextInput } from "../../components/inputs/textInput";
import { HeaderSubheader } from "../../components/typography/headerSubheader";
import {
  useForgotPasswordMutation,
  useLazyGetUserProfileQuery,
  useResendVerificationCodeMutation,
  useSignInMutation,
  useVerifyEmailMutation,
} from "../../redux/features/auth/auth.api";
import { AuthSliceActions } from "../../redux/features/auth/auth.slice";
import { useAppDispatch } from "../../redux/hooks";
import { loginSchema, loginSchema1 } from "../../validationSchema";

export const SignIn = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [signIn, { isLoading: loggingIn }] = useSignInMutation();

  const [verifyEmail, { isLoading: verifyingEmail }] = useVerifyEmailMutation();

  const [forgotPassword, { isLoading: sendingCode }] =
    useForgotPasswordMutation();

  const [
    resendAccountVerificationCode,
    { isLoading: sendingVerificationCode },
  ] = useResendVerificationCodeMutation();

  const [getUserProfile, { isFetching: fetchingProfile }] =
    useLazyGetUserProfileQuery();

  const [step, setStep] = useState(1);

  const {
    values,
    errors,
    isValid,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
  } = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: step === 1 ? loginSchema1 : loginSchema,
    onSubmit: async (data, { setSubmitting }) => {
      setSubmitting(true);

      if (step === 1) {
        const resp = await verifyEmail({
          Email: data.email,
        });

        if (resp?.data?.success) {
          if (!resp.data?.data?.isActive) {
            // Send forgot password code and go to forgot password for inactive users to set password
            const forgotResp = await forgotPassword({
              Email: data.email,
            });

            if (forgotResp?.data?.success) {
              navigate(
                `/auth/forgot-password?email=${encodeURIComponent(
                  data.email
                )}&step=activateAccount`
              );
            }

            return;
          }

          if (!resp?.data?.data?.isVerified) {
            // Do email verification
            const verifyResp = await resendAccountVerificationCode({
              Email: data.email,
            });

            if (verifyResp?.data?.success) {
              navigate(
                `/auth/sign-up?step=confirm-mail&email=${encodeURIComponent(
                  data.email
                )}`
              );
            }

            return;
          }

          setStep(2);
        }
      }

      // Do actual login
      if (step === 2) {
        const payload = {
          username: data.email,
          password: data.password,
        };

        const resp = await signIn({
          ...payload,
        });

        if (resp.data?.access) {
          const profileResp = await getUserProfile();

          if (profileResp?.data?.success) {
            dispatch(AuthSliceActions.login());

            navigate("/app/overview");

            localStorage.removeItem("resumeLink");
          }
        }
      }
    },
  });

  useEffect(() => {
    setStep(1);
    // eslint-disable-next-line
  }, [values?.email]);

  return (
    <div className="!w-full !max-w-[327px]">
      <HeaderSubheader
        header="Welcome Back"
        subheader="Start managing your finances faster and better."
        className=""
      />

      <form className="w-full mt-6 flex flex-col gap-3" onSubmit={handleSubmit}>
        <TextInput
          type="text"
          name="email"
          value={values.email}
          placeholder="Email Address"
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors.email && touched.email ? errors.email : ""}
          autoComplete="on"
        />

        {step === 2 && (
          <TextInput
            type="password"
            name="password"
            value={values.password}
            placeholder="Password"
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.password && touched.password ? errors.password : ""}
            autoComplete="on"
          />
        )}

        {step === 2 && (
          <div className="w-full text-right">
            <TextLink
              to={`/auth/forgot-password${
                !errors.email
                  ? `?email=${encodeURIComponent(values.email)}`
                  : ""
              }`}
              text="Forgot Password?"
              className="!text-xs"
            />
          </div>
        )}

        <Button
          text={step === 2 ? "Login" : "Continue"}
          disabled={
            !isValid ||
            verifyingEmail ||
            sendingCode ||
            loggingIn ||
            fetchingProfile ||
            sendingVerificationCode
          }
          type="submit"
          loading={
            verifyingEmail ||
            sendingCode ||
            loggingIn ||
            fetchingProfile ||
            sendingVerificationCode
          }
          className="mt-3"
        />

        <div className="w-full mt-1 !text-sm text-grey-400">
          Don’t have an account?{" "}
          <TextLink to="/auth/sign-up" text="Register" className="" />
        </div>
      </form>
    </div>
  );
};
